import { useCallback } from "react";
import {
  useUpdateLessonCompleteStatusMutation,
  useUpdateSectionCompleteStatusMutation,
  useHeaUpdateLessonStatusMutation,
  SectionFragment,
  SectionFragmentDoc,
  useSllUpdateLessonCompleteStatusMutation,
  useUpdateUnitStudyReviewStatusMutation,
  SllLesson,
} from "@api/graphql-codegen";
import { createPrettyURL, isHomeEDApp } from "@constants";
import { resolveStandaloneMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import { resolveUnitMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import {
  resolveMixpanelProgramName,
  mixpanelMultiCollectionProps,
  resolveMixpanelUnitGrade,
  resolveMixpanelStandaloneGrade,
} from "@src/utils/mixpanelProgramType";
import { resolveLessonGradeRange, resolveMcAnalyticsGrade } from "@src/utils/mcAnalyticsGrade";
import { mixpanelCompetencyProps } from "@src/utils/mixpanelCompetency";
import { mixpanelSkillProps } from "@src/utils/mixpanelSkill";
import { mixpanelMoodProps } from "@src/utils/mixpanelMood";
import { useAnalytics, Events } from "@hooks/useAnalytics";
import { LessonStatus } from "@src/types";
import { useUser } from "./useUser";
import { useStandaloneContent } from "./useStandaloneContent";
import { logger } from "../utils/logger";

export const useMarkLessonComplete = (
  onComplete?: () => void,
  goBack?: () => void,
  section?: SectionFragment,
) => {
  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  // mcId identifies the multi-collection card the lesson was launched from.
  const { mcId, mcName, launchedGrade } = useStandaloneContent();
  const [heaUpdateLessonComplete, { loading: heaLoading }] =
    useHeaUpdateLessonStatusMutation({
      onError(e) {
        logger.debug(`[e] useHeaUpdateLessonStatusMutation -`, e);
      },
    });

  const [updateLessonComplete, { loading: lessonLoading }] =
    useUpdateLessonCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e] - useUpdateLessonCompleteStatusMutation`, e);
      },
    });

  const [updateSectionComplete, { loading: sectionLoading }] =
    useUpdateSectionCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e] - useUpdateSectionCompleteStatusMutation`, e);
      },
      update(cache, res, { variables }) {
        const d = res?.data?.updateSectionCompleteStatus;
        if (d && section?.id) {
          cache.updateFragment(
            {
              id: `Section:${section?.id}`,
              fragment: SectionFragmentDoc,
              fragmentName: "Section",
            },
            (l) => ({
              ...l,
              status: variables?.input.status || LessonStatus.Completed,
            }),
          );
        }
      },
    });

  const [sllUpdateLessonComplete, { loading: sllLessonLoading }] =
    useSllUpdateLessonCompleteStatusMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
    });

  const [updateUnitStudyReviewStatus, { loading: studyReviewLoading }] =
    useUpdateUnitStudyReviewStatusMutation({
      onError(e) {
        logger.debug(`[e] - useUpdateUnitStudyReviewStatusMutation`, e);
      },
    });

  const markLessonComplete = useCallback(
    async (
      unitId: string,
      lessonId: string,
      studyPlanId?: string,
      trackData?: {
        unit_name?: string;
        unit_id?: string;
        unit_unique_name?: string;
        program_name?: string;
        course_name?: string;
        course_id?: string;
        lesson_name?: string;
        lesson_id?: string;
        lesson_position?: number;
        study_plan_id?: string;
        study_plan_name?: string;
        section_id?: string;
        grade?: string;
        competency?: string;
        skill?: string;
        categories?: string;
      },
      isOnlyNotCompletedLesson?: boolean,
      unitProgress?: number | null,
      isChild?: boolean,
    ) => {
      try {
        // categories is only for mood resolution — do not send raw JSON to Mixpanel
        const { categories: trackCategories, ...trackDataRest } =
          trackData ?? {};
        let didPersist = false;
        if (isHomeEDApp) {
          if (studyPlanId) {
            await heaUpdateLessonComplete({
              variables: {
                input: {
                  studyplanId: studyPlanId,
                  unitId,
                  lessonId,
                  status: isChild
                    ? LessonStatus.Completed
                    : LessonStatus.Confirmed,
                  studyPlanName: isChild
                    ? trackDataRest?.study_plan_name
                    : undefined,
                  lessonName: isChild ? trackDataRest?.lesson_name : undefined,
                  unitName: isChild ? trackDataRest?.unit_name : undefined,
                },
              },
            });
            didPersist = true;
            const updatedTrackData = {
              ...trackDataRest,
              study_plan_id: studyPlanId,
              lesson_id: lessonId,
              unit_id: unitId,
              grade: resolveMixpanelUnitGrade({
                courseName: trackDataRest?.course_name,
                grade: trackDataRest?.grade,
              }),
              ...mixpanelCompetencyProps(trackDataRest?.competency),
              ...mixpanelSkillProps({ skill: trackDataRest?.skill }),
              ...mixpanelMoodProps({ categories: trackCategories }),
              ...mixpanelMultiCollectionProps(),
              lessonType: resolveUnitMixpanelLessonType({
                unitName: trackDataRest?.unit_name,
                unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
              program_name: resolveMixpanelProgramName({
                programName: trackDataRest?.program_name,
                courseName: trackDataRest?.course_name,
                unitName: trackDataRest?.unit_name,
                unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
            };
            trackEvent(Events.actions.lessonCompleted, {
              ...updatedTrackData,
              type: "Unit",
            });
            if (isOnlyNotCompletedLesson) {
              trackEvent(Events.actions.allLessonsCompletedInUnit, {
                ...updatedTrackData,
                type: "Unit",
              });
            }
          }
        } else {
          if (section?.id) {
            await updateSectionComplete({
              variables: {
                input: {
                  sectionId: section?.id,
                  unitId,
                  lessonId,
                  status: LessonStatus.Completed,
                },
              },
            });
            didPersist = true;
            trackEvent(Events.actions.sectionCompleted, {
              ...trackDataRest,
              grade: resolveMixpanelUnitGrade({
                courseName: trackDataRest?.course_name,
                grade: trackDataRest?.grade,
              }),
              ...mixpanelCompetencyProps(trackDataRest?.competency),
              ...mixpanelSkillProps({ skill: trackDataRest?.skill }),
              ...mixpanelMoodProps({ categories: trackCategories }),
              ...mixpanelMultiCollectionProps(),
              program_name: resolveMixpanelProgramName({
                programName: trackDataRest?.program_name,
                courseName: trackDataRest?.course_name,
                unitName: trackDataRest?.unit_name,
                unitId: trackDataRest?.unit_id || unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
              lessonType: resolveUnitMixpanelLessonType({
                unitName: trackDataRest?.unit_name,
                unitId: trackDataRest?.unit_id || unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
              type: "Unit",
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} + ' ' + ${user?.lastName}`,
            });
            if (
              section?.name &&
              section?.name.toLowerCase() === "exit ticket"
            ) {
              await updateLessonComplete({
                variables: {
                  input: {
                    unitId,
                    lessonId,
                    status: LessonStatus.Completed,
                  },
                },
              });
              const exitCourseName =
                trackDataRest?.unit_name === "Educator Onboarding"
                  ? "Educator Onboarding"
                  : trackDataRest?.unit_name === "Educator Wellbeing"
                    ? "Educator Wellbeing"
                    : trackDataRest?.course_name;
              trackEvent(Events.actions.lessonCompleted, {
                ...trackDataRest,
                course_name: exitCourseName,
                grade: resolveMixpanelUnitGrade({
                  courseName: exitCourseName,
                  grade: trackDataRest?.grade,
                }),
                ...mixpanelCompetencyProps(trackDataRest?.competency),
                ...mixpanelSkillProps({ skill: trackDataRest?.skill }),
                ...mixpanelMoodProps({ categories: trackCategories }),
                ...mixpanelMultiCollectionProps(),
                program_name: resolveMixpanelProgramName({
                  programName: trackDataRest?.program_name,
                  courseName: trackDataRest?.course_name,
                  unitName: trackDataRest?.unit_name,
                  unitId: trackDataRest?.unit_id || unitId,
                  unitUniqueName: trackDataRest?.unit_unique_name,
                }),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: trackDataRest?.unit_name,
                  unitId: trackDataRest?.unit_id || unitId,
                  unitUniqueName: trackDataRest?.unit_unique_name,
                }),
                type: "Unit",
                schoolId: user?.schools?.[0]?.id,
                schoolName: user?.schools?.[0]?.name,
                userId: user?.id,
                userName: `${user?.firstName} ${user?.lastName}`,
              });
            }
          } else {
            await updateLessonComplete({
              variables: {
                input: {
                  unitId,
                  lessonId,
                  status: LessonStatus.Completed,
                },
              },
            });
            didPersist = true;

            const courseName =
              trackDataRest?.unit_name === "Educator Onboarding"
                ? "Educator Onboarding"
                : trackDataRest?.unit_name === "Educator Wellbeing"
                  ? "Educator Wellbeing"
                  : trackDataRest?.course_name;
            const resolvedProgramName = resolveMixpanelProgramName({
              programName: trackDataRest?.program_name,
              courseName,
              unitName: trackDataRest?.unit_name,
              unitId: trackDataRest?.unit_id || unitId,
              unitUniqueName: trackDataRest?.unit_unique_name,
            });
            const updatedTrackData = {
              unit_name: trackDataRest?.unit_name || "",
              unit_id: trackDataRest?.unit_id || "",
              course_name: courseName || "",
              course_id: trackDataRest?.course_id || "",
              lesson_name: trackDataRest?.lesson_name || "",
              lesson_id: trackDataRest?.lesson_id || "",
              lesson_position: trackDataRest?.lesson_position || 0,
              grade: resolveMixpanelUnitGrade({
                courseName,
                grade: trackDataRest?.grade,
              }),
              ...mixpanelCompetencyProps(trackDataRest?.competency),
              ...mixpanelSkillProps({ skill: trackDataRest?.skill }),
              ...mixpanelMoodProps({ categories: trackCategories }),
              ...mixpanelMultiCollectionProps(),
              program_name: resolvedProgramName,
              lessonType: resolveUnitMixpanelLessonType({
                unitName: trackDataRest?.unit_name,
                unitId: trackDataRest?.unit_id || unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} ${user?.lastName}`,
            };
            if (
              trackDataRest?.unit_name === "Educator Onboarding" ||
              trackDataRest?.unit_name === "Educator Wellbeing"
            ) {
              trackEvent(Events.actions.lessonCompleted, {
                ...updatedTrackData,
                type: "Unit",
              });
            } else {
              trackEvent(Events.actions.lessonCompleted, {
                ...updatedTrackData,
                type: "Unit",
              });
            }
          }
          if (isOnlyNotCompletedLesson) {
            if (!section?.id) {
              trackEvent(Events.actions.allLessonsCompletedInUnit, {
                ...trackDataRest,
                grade: resolveMixpanelUnitGrade({
                  courseName: trackDataRest?.course_name,
                  grade: trackDataRest?.grade,
                }),
                ...mixpanelCompetencyProps(trackDataRest?.competency),
                ...mixpanelSkillProps({ skill: trackDataRest?.skill }),
                ...mixpanelMoodProps({ categories: trackCategories }),
                ...mixpanelMultiCollectionProps(),
                program_name: resolveMixpanelProgramName({
                  programName: trackDataRest?.program_name,
                  courseName: trackDataRest?.course_name,
                  unitName: trackDataRest?.unit_name,
                  unitId: trackDataRest?.unit_id || unitId,
                  unitUniqueName: trackDataRest?.unit_unique_name,
                }),
                lessonType: resolveUnitMixpanelLessonType({
                  unitName: trackDataRest?.unit_name,
                  unitId: trackDataRest?.unit_id || unitId,
                  unitUniqueName: trackDataRest?.unit_unique_name,
                }),
                type: "Unit",
                schoolId: user?.schools?.[0]?.id,
                schoolName: user?.schools?.[0]?.name,
                userId: user?.id,
                userName: `${user?.firstName} ${user?.lastName}`,
              });
            }
          } else if (!unitProgress) {
            trackEvent(Events.actions.startUnit, {
              ...trackDataRest,
              grade: resolveMixpanelUnitGrade({
                courseName: trackDataRest?.course_name,
                grade: trackDataRest?.grade,
              }),
              ...mixpanelCompetencyProps(trackDataRest?.competency),
              ...mixpanelSkillProps({ skill: trackDataRest?.skill }),
              ...mixpanelMoodProps({ categories: trackCategories }),
              ...mixpanelMultiCollectionProps(),
              program_name: resolveMixpanelProgramName({
                programName: trackDataRest?.program_name,
                courseName: trackDataRest?.course_name,
                unitName: trackDataRest?.unit_name,
                unitId: trackDataRest?.unit_id || unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
              lessonType: resolveUnitMixpanelLessonType({
                unitName: trackDataRest?.unit_name,
                unitId: trackDataRest?.unit_id || unitId,
                unitUniqueName: trackDataRest?.unit_unique_name,
              }),
              type: "Unit",
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} + ${user?.lastName}`,
            });
          }
        }
        if (didPersist && onComplete) {
          onComplete();
        }
      } catch (e) {
        logger.debug(`[e] markLessonComplete - `, e);
      }
      if (goBack && !isChild) {
        goBack();
      }
    },
    [
      user,
      goBack,
      heaUpdateLessonComplete,
      onComplete,
      section,
      trackEvent,
      updateLessonComplete,
      updateSectionComplete,
    ],
  );

  const lessonNotStarted = useCallback(
    async (unitId: string, lessonId: string) => {
      try {
        if (section?.id) {
          await updateSectionComplete({
            variables: {
              input: {
                sectionId: section?.id,
                unitId,
                lessonId,
                status: LessonStatus.NotStarted,
              },
            },
          });
        } else {
          await updateLessonComplete({
            variables: {
              input: {
                unitId,
                lessonId,
                status: LessonStatus.NotStarted,
              },
            },
          });
        }
      } catch (e) {
        logger.debug(`[e] lessonNotStarted - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, section?.id, updateLessonComplete, updateSectionComplete],
  );

  const heUpdateLessonInProgress = useCallback(
    async (unitId: string, lessonId: string, studyPlanId: string) => {
      if (!isHomeEDApp) {
        return;
      }
      try {
        await heaUpdateLessonComplete({
          variables: {
            input: {
              studyplanId: studyPlanId,
              unitId,
              lessonId,
              status: LessonStatus.InProgress,
            },
          },
        });
      } catch (e) {
        logger.debug(`[e] heaUpdateLessonInProgress - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, heaUpdateLessonComplete],
  );

  const sllMarkLessonComplete = useCallback(
    async (l: SllLesson, programName?: string | undefined) => {
      try {
        await sllUpdateLessonComplete({
          variables: {
            input: {
              lessonId: l.id,
              status: LessonStatus.Completed,
            },
          },
        });
        const resolvedLessonType = resolveStandaloneMixpanelLessonType({
          lesson: { uniqueName: l.uniqueName, lessonType: l.lessonType },
          programName,
        });
        const resolvedProgramName = resolveMixpanelProgramName({
          lessonType: l.lessonType,
          programType: l.multiCollections?.[0]?.programType,
          programName,
          multiCollectionName: l.multiCollections?.[0]?.name ?? mcName,
        });
        trackEvent(Events.actions.lessonCompleted, {
          lesson_id: l.id,
          lesson_name: l.name,
          // OP-3092: a multi-grade lesson launched from a single-grade card reports that card's
          // grade; every other case falls through to the lesson's own range.
          grade:
            resolveMcAnalyticsGrade({
              lessonGrade: resolveLessonGradeRange(l.ageGroup, l.gradeLevel),
              multiCollections: l.multiCollections,
              mcId,
              launchedGrade,
            }) ??
            resolveMixpanelStandaloneGrade({
              ageGroup: l.ageGroup,
              gradeLevel: l.gradeLevel,
            }),
          ...(mcId && { mc_id: mcId }),
          ...mixpanelSkillProps({
            categories: l.categories,
            skill: l.skill,
          }),
          ...mixpanelMoodProps({ categories: l.categories }),
          ...mixpanelCompetencyProps(l.competency),
          ...(l.collections &&
            l.collections.length && {
              collection_name: l.collections.filter(
                (c) => c?.collectionType === "Theme",
              )[0]?.name,
            }),
          program_name: resolvedProgramName,
          ...mixpanelMultiCollectionProps({
            multiCollections: l.multiCollections,
            mcName,
          }),
          lessonType: resolvedLessonType,
          originalURL: createPrettyURL(`standalone/${l.uniqueName}`),
          type: "Standalone",
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });

        if (onComplete) {
          onComplete();
        }
      } catch (e) {
        logger.debug(`[e] markLessonComplete - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [
      goBack,
      sllUpdateLessonComplete,
      onComplete,
      trackEvent,
      user,
      mcName,
      mcId,
      launchedGrade,
    ],
  );

  const sllLessonNotStarted = useCallback(
    async (lessonId: string) => {
      try {
        await sllUpdateLessonComplete({
          variables: {
            input: {
              lessonId,
              status: LessonStatus.NotStarted,
            },
          },
        });
      } catch (e) {
        logger.debug(`[e] lessonNotStarted - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, sllUpdateLessonComplete],
  );

  const markStudyReviewComplete = useCallback(
    async (unitId: string) => {
      try {
        await updateUnitStudyReviewStatus({
          variables: {
            input: {
              unitId,
              status: LessonStatus.Completed,
            },
          },
        });
        if (onComplete) {
          onComplete();
        }
      } catch (e) {
        logger.debug(`[e] markLessonComplete - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, updateUnitStudyReviewStatus, onComplete],
  );

  const studyReviewNotStarted = useCallback(
    async (unitId: string) => {
      try {
        await updateUnitStudyReviewStatus({
          variables: {
            input: {
              unitId,
              status: LessonStatus.NotStarted,
            },
          },
        });
      } catch (e) {
        logger.debug(`[e] lessonNotStarted - `, e);
      }
      if (goBack) {
        goBack();
      }
    },
    [goBack, updateUnitStudyReviewStatus],
  );

  return {
    markLessonComplete,
    lessonNotStarted,
    heUpdateLessonInProgress,
    sllMarkLessonComplete,
    sllLessonNotStarted,
    markStudyReviewComplete,
    studyReviewNotStarted,
    loading:
      heaLoading ||
      lessonLoading ||
      sectionLoading ||
      sllLessonLoading ||
      studyReviewLoading,
  };
};
